import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, ArrowRightCircle, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { mget, mpost, mput, mdel, errMsg, eur, fmtDate, LEAD_STATUS, SOURCE_LABEL } from "@/manager/lib";
import { PageHeader, Card, Field, Pill, Empty, goldBtn } from "@/manager/ui";

const BLANK = { name: "", phone: "", email: "", event_date: "", guests: "", event_type: "", kind: "sala", budget: "", status: "nuevo", notes: "", next_followup: "" };
const COLS = ["nuevo", "contactado", "presupuesto", "negociacion", "confirmado"];
const num = (v) => (v === "" || v == null ? null : Number(v));

function LeadCard({ l, onEdit, onConvert, onOpenEvent, onDelete }) {
  return (
    <div className="bg-white border border-[#e6e1d6] rounded-xl p-3 space-y-1" data-testid={`lead-${l.id}`}>
      <div className="flex justify-between gap-2"><p className="text-sm font-medium">{l.name}</p><span className="flex gap-2"><button onClick={() => onEdit(l)} data-testid={`lead-edit-${l.id}`}><Pencil size={13} /></button><button onClick={() => onDelete(l)} className="text-rose-700" data-testid={`lead-delete-${l.id}`}><Trash2 size={13} /></button></span></div>
      <p className="text-xs text-[#6b6a66]">{l.event_date ? fmtDate(l.event_date) : "Sin fecha"} · {l.guests ?? "?"} pers. · {l.kind === "externo" ? "Externo" : "Sala"}</p>
      {l.phone && <a href={`https://wa.me/${l.phone.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="text-xs underline text-[#9a7f3f]">{l.phone}</a>}
      {l.notes && <p className="text-xs text-[#6b6a66] line-clamp-2">{l.notes}</p>}
      <div className="flex flex-wrap items-center gap-1 pt-1">
        {l.external_source && <Pill className="bg-[#F7F5F0] text-[#6b6a66]">{SOURCE_LABEL[l.external_source]}</Pill>}
        {l.budget != null && <Pill className="bg-[#F7F5F0] text-[#6b6a66]">{eur(l.budget)}</Pill>}
        {l.next_followup && <Pill className="bg-amber-50 text-amber-800">Seguir {fmtDate(l.next_followup)}</Pill>}
      </div>
      {l.event_id ? <button onClick={() => onOpenEvent(l.event_id)} className="text-xs text-emerald-700 underline" data-testid={`lead-open-event-${l.id}`}>Ver evento</button>
        : <button onClick={() => onConvert(l)} className="text-xs flex items-center gap-1 text-[#101B28] hover:underline" data-testid={`lead-convert-${l.id}`}><ArrowRightCircle size={13} /> Convertir en evento</button>}
    </div>
  );
}

export default function Crm() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => mget("/leads").then(setLeads), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (params.get("new") === "1") { setEdit(BLANK); setParams({}); } }, [params, setParams]);
  const save = async (e) => {
    e.preventDefault();
    const body = { ...edit, guests: num(edit.guests), budget: num(edit.budget) };
    try { if (edit.id) await mput(`/leads/${edit.id}`, body); else await mpost("/leads", body); setEdit(null); load(); toast.success("Guardado"); } catch (err) { toast.error(errMsg(err)); }
  };
  const convert = async (l) => {
    try { const ev = await mpost(`/leads/${l.id}/convert`); toast.success("Evento creado con sus tareas"); navigate(`/manager/eventos/${ev.id}`); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (l) => {
    if (!window.confirm(`¿Eliminar a ${l.name} del CRM?`)) return;
    try { await mdel(`/leads/${l.id}`); load(); toast.success("Eliminado"); } catch (e) { toast.error(errMsg(e)); }
  };
  const set = (k) => (e) => setEdit({ ...edit, [k]: e.target.value });
  const closed = leads.filter((l) => ["perdido", "cancelado"].includes(l.status));

  return (
    <div data-testid="manager-crm">
      <PageHeader eyebrow="CRM" title="Interesados y seguimiento">
        <Button className={goldBtn} onClick={() => setEdit(BLANK)} data-testid="lead-new"><Plus size={15} /> Interesado</Button>
      </PageHeader>
      <p className="text-sm text-[#6b6a66] mb-4">Las solicitudes del formulario de la web entran aquí solas como «Nuevo». Las peticiones de eventos externos aparecen directamente en la Agenda como provisionales.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-3">
        {COLS.map((c) => {
          const items = leads.filter((l) => l.status === c);
          return (
            <div key={c} className="bg-[#efebe2] rounded-2xl p-3" data-testid={`crm-col-${c}`}>
              <p className="text-[11px] uppercase tracking-[0.16em] text-[#6b6a66] mb-3">{LEAD_STATUS[c]} · {items.length}</p>
              <div className="space-y-2 max-h-[70vh] overflow-y-auto">{items.map((l) => <LeadCard key={l.id} l={l} onEdit={setEdit} onConvert={convert} onOpenEvent={(id) => navigate(`/manager/eventos/${id}`)} onDelete={remove} />)}</div>
            </div>
          );
        })}
      </div>
      {closed.length > 0 && <p className="text-xs text-[#6b6a66] mt-4">{closed.length} perdidos/cancelados archivados.</p>}
      {!leads.length && <Card className="mt-4"><Empty>Sin interesados.</Empty></Card>}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="bg-white max-w-xl max-h-[90vh] overflow-y-auto" data-testid="lead-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{edit?.id ? "Editar interesado" : "Nuevo interesado"}</DialogTitle></DialogHeader>
          {edit && (
            <form onSubmit={save} className="grid grid-cols-2 gap-3">
              <Field label="Nombre *" className="col-span-2"><Input value={edit.name} onChange={set("name")} required data-testid="lead-name" /></Field>
              <Field label="Teléfono"><Input value={edit.phone} onChange={set("phone")} data-testid="lead-phone" /></Field>
              <Field label="Email"><Input value={edit.email} onChange={set("email")} data-testid="lead-email" /></Field>
              <Field label="Fecha del evento"><Input type="date" value={edit.event_date} onChange={set("event_date")} data-testid="lead-date" /></Field>
              <Field label="Invitados"><Input type="number" value={edit.guests ?? ""} onChange={set("guests")} data-testid="lead-guests" /></Field>
              <Field label="Tipo de evento"><Input value={edit.event_type} onChange={set("event_type")} data-testid="lead-type" /></Field>
              <Field label="Presupuesto (€)"><Input type="number" value={edit.budget ?? ""} onChange={set("budget")} data-testid="lead-budget" /></Field>
              <Field label="Línea">
                <Select value={edit.kind} onValueChange={(v) => setEdit({ ...edit, kind: v })}><SelectTrigger data-testid="lead-kind"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="sala">Sala</SelectItem><SelectItem value="externo">Externo</SelectItem></SelectContent></Select>
              </Field>
              <Field label="Estado">
                <Select value={edit.status} onValueChange={(v) => setEdit({ ...edit, status: v })}><SelectTrigger data-testid="lead-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(LEAD_STATUS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
              </Field>
              <Field label="Próximo seguimiento" className="col-span-2"><Input type="date" value={edit.next_followup} onChange={set("next_followup")} data-testid="lead-followup" /></Field>
              <Field label="Notas" className="col-span-2"><Textarea rows={3} value={edit.notes} onChange={set("notes")} data-testid="lead-notes" /></Field>
              <DialogFooter className="col-span-2"><Button type="submit" className={goldBtn} data-testid="lead-save">Guardar</Button></DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
